import { salesApi } from '../../../../config/apiClient';
import type { PackageOrder } from '../types';

/**
 * Paket sipariş kara listesi (telefon siparişi + Yemeksepeti, Uber Eats, Migros, Getir müşterileri).
 * Sunucu: gts_sales_api CustomerBlacklist. Sunucu, sipariş kimliğinden müşteri numarasını, cariyi,
 * telefonu ve adresi kendisi okur; ekranın göndermesi gereken yalnız platform + sipariş kimliğidir.
 */

export type BlacklistPlatform = 'phone' | 'yemeksepeti' | 'trendyol' | 'migros' | 'getir';
export type BlacklistReason = 'unpaid' | 'fake' | 'not_received' | 'refund_abuse' | 'behavior' | 'other';
export type BlacklistKeyType = 'account' | 'phone' | 'trendyol' | 'migros' | 'getir' | 'yemeksepeti';
export type BlacklistAction = 'accepted' | 'rejected' | 'dismissed';

export const BLACKLIST_REASONS: BlacklistReason[] = ['unpaid', 'fake', 'not_received', 'refund_abuse', 'behavior', 'other'];
export const BLACKLIST_PLATFORMS: BlacklistPlatform[] = ['phone', 'yemeksepeti', 'trendyol', 'migros', 'getir'];

export interface BlacklistKey {
  id?: number | null;
  type: BlacklistKeyType;
  value: string;
  display?: string | null;
  usable: boolean;
  warning?: string | null;
  existingEntryId?: number | null;
}

export interface BlacklistEntry {
  id: number;
  displayName: string;
  /** '' = bütün şubeler */
  branchId: string;
  branchName?: string | null;
  ownerBranchId: string;
  ownerBranchName?: string | null;
  accountId?: number | null;
  address?: string | null;
  reason: BlacklistReason;
  note?: string | null;
  sourcePlatform?: string | null;
  sourceOrderRef?: string | null;
  isActive: boolean;
  hitCount: number;
  lastHitAt?: string | null;
  lastAction?: string | null;
  lastActionAt?: string | null;
  lastActionByName?: string | null;
  createdByName?: string | null;
  createdAt: string;
  updatedByName?: string | null;
  updatedAt?: string | null;
  removedByName?: string | null;
  removedAt?: string | null;
  removeNote?: string | null;
  platforms: string[];
  keys: BlacklistKey[];
  canManage: boolean;
}

/** Siparişi / müşteriyi tanıtan bilgi. Sunucu sipariş kaydından eksikleri tamamlar. */
export interface BlacklistSubject {
  platform: BlacklistPlatform;
  /** phone / yemeksepeti / migros / getir = yerel sipariş Id; trendyol = paket Id */
  orderRef?: string | null;
  name?: string | null;
  phone?: string | null;
  address?: string | null;
  accountId?: number | null;
  amount?: number | null;
}

export interface BlacklistPrepareResult {
  displayName: string;
  address?: string | null;
  platform: string;
  orderRef?: string | null;
  accountId?: number | null;
  keys: BlacklistKey[];
  existing?: BlacklistEntry | null;
}

export interface BlacklistMatch {
  entryId: number;
  displayName: string;
  reason: BlacklistReason;
  note?: string | null;
  createdByName?: string | null;
  createdAt: string;
  ownerBranchName?: string | null;
  matchedBy: string[];
  hitCount: number;
  lastAction?: string | null;
  lastActionAt?: string | null;
}

export interface BlacklistCheckResult {
  platform: string;
  orderRef?: string | null;
  /** Kesin eşleşme: cari, telefon, platform müşteri no */
  match?: BlacklistMatch | null;
  /** Olası eşleşme: aynı ad + benzer adres (yalnız sarı not) */
  possible?: BlacklistMatch | null;
}

export interface BlacklistHit {
  id: number;
  platform: string;
  orderRef: string;
  branchName?: string | null;
  matchedBy?: string | null;
  amount?: number | null;
  action: string;
  actionByName?: string | null;
  actionAt?: string | null;
  createdAt: string;
}

export interface BlacklistCreateInput {
  platform?: string | null;
  orderRef?: string | null;
  displayName: string;
  address?: string | null;
  accountId?: number | null;
  keys: Pick<BlacklistKey, 'type' | 'value' | 'display'>[];
  reason: BlacklistReason;
  note?: string | null;
  onlyBranch: boolean;
  branchId?: string | null;
}

export interface BlacklistUpdateInput {
  id: number;
  displayName?: string | null;
  reason?: BlacklistReason | null;
  note?: string | null;
  onlyBranch?: boolean | null;
  branchId?: string | null;
  addKeys?: Pick<BlacklistKey, 'type' | 'value' | 'display'>[];
  removeKeyIds?: number[];
}

const BASE = '/api/CustomerBlacklist';

const num = (v: any): number => (typeof v === 'number' ? v : Number(v ?? 0) || 0);
const pick = (o: any, name: string): any => (o ? o[name] ?? o[name.charAt(0).toUpperCase() + name.slice(1)] : undefined);

const unwrap = (res: any): any => {
  const body = res?.data;
  if (body && (body.Success === false || body.success === false)) {
    throw new Error(body.Message || body.message || 'İşlem başarısız');
  }
  return body?.Data ?? body?.data;
};

const errorMessage = (e: any, fallback: string): string =>
  e?.response?.data?.Message || e?.response?.data?.message || e?.message || fallback;

const call = async (path: string, body: any, fallback: string): Promise<any> => {
  try {
    return unwrap(await salesApi.post(`${BASE}/${path}`, body));
  } catch (e) {
    throw new Error(errorMessage(e, fallback));
  }
};

const toKey = (k: any): BlacklistKey => ({
  id: pick(k, 'id') ?? null,
  type: pick(k, 'type'),
  value: String(pick(k, 'value') ?? ''),
  display: pick(k, 'display') ?? null,
  usable: pick(k, 'usable') !== false,
  warning: pick(k, 'warning') ?? null,
  existingEntryId: pick(k, 'existingEntryId') ?? null,
});

const toEntry = (e: any): BlacklistEntry => ({
  id: num(pick(e, 'id')),
  displayName: pick(e, 'displayName') || '',
  branchId: pick(e, 'branchId') || '',
  branchName: pick(e, 'branchName') ?? null,
  ownerBranchId: pick(e, 'ownerBranchId') || '',
  ownerBranchName: pick(e, 'ownerBranchName') ?? null,
  accountId: pick(e, 'accountId') ?? null,
  address: pick(e, 'address') ?? null,
  reason: (pick(e, 'reason') || 'other') as BlacklistReason,
  note: pick(e, 'note') ?? null,
  sourcePlatform: pick(e, 'sourcePlatform') ?? null,
  sourceOrderRef: pick(e, 'sourceOrderRef') ?? null,
  isActive: pick(e, 'isActive') !== false,
  hitCount: num(pick(e, 'hitCount')),
  lastHitAt: pick(e, 'lastHitAt') ?? null,
  lastAction: pick(e, 'lastAction') ?? null,
  lastActionAt: pick(e, 'lastActionAt') ?? null,
  lastActionByName: pick(e, 'lastActionByName') ?? null,
  createdByName: pick(e, 'createdByName') ?? null,
  createdAt: pick(e, 'createdAt') || '',
  updatedByName: pick(e, 'updatedByName') ?? null,
  updatedAt: pick(e, 'updatedAt') ?? null,
  removedByName: pick(e, 'removedByName') ?? null,
  removedAt: pick(e, 'removedAt') ?? null,
  removeNote: pick(e, 'removeNote') ?? null,
  platforms: pick(e, 'platforms') || [],
  keys: (pick(e, 'keys') || []).map(toKey),
  canManage: !!pick(e, 'canManage'),
});

const toMatch = (m: any): BlacklistMatch | null =>
  m
    ? {
      entryId: num(pick(m, 'entryId')),
      displayName: pick(m, 'displayName') || '',
      reason: (pick(m, 'reason') || 'other') as BlacklistReason,
      note: pick(m, 'note') ?? null,
      createdByName: pick(m, 'createdByName') ?? null,
      createdAt: pick(m, 'createdAt') || '',
      ownerBranchName: pick(m, 'ownerBranchName') ?? null,
      matchedBy: pick(m, 'matchedBy') || [],
      hitCount: num(pick(m, 'hitCount')),
      lastAction: pick(m, 'lastAction') ?? null,
      lastActionAt: pick(m, 'lastActionAt') ?? null,
    }
    : null;

const subjectBody = (s: BlacklistSubject) => ({
  Platform: s.platform,
  OrderRef: s.orderRef || null,
  Name: s.name || null,
  Phone: s.phone || null,
  Address: s.address || null,
  AccountId: s.accountId ?? null,
  Amount: s.amount ?? null,
});

const keyBody = (k: Pick<BlacklistKey, 'type' | 'value' | 'display'>) => ({ Type: k.type, Value: k.value, Display: k.display || null });

export const customerBlacklistService = {
  /** Kara liste penceresi. branchId boşsa şirket görünümü. */
  async list(p: { branchId?: string | null; includeRemoved?: boolean; search?: string }): Promise<BlacklistEntry[]> {
    const d = await call('list', { BranchId: p.branchId || null, IncludeRemoved: !!p.includeRemoved, Search: p.search || null }, 'Kara liste yüklenemedi');
    return (d || []).map(toEntry);
  },

  async get(id: number, branchId?: string | null): Promise<BlacklistEntry> {
    return toEntry(await call('get', { Id: id, BranchId: branchId || null }, 'Kayıt yüklenemedi'));
  },

  async hits(id: number): Promise<BlacklistHit[]> {
    const d = await call('hits', { Id: id }, 'Geçmiş yüklenemedi');
    return (d || []).map((h: any): BlacklistHit => ({
      id: num(pick(h, 'id')),
      platform: pick(h, 'platform') || '',
      orderRef: pick(h, 'orderRef') || '',
      branchName: pick(h, 'branchName') ?? null,
      matchedBy: pick(h, 'matchedBy') ?? null,
      amount: pick(h, 'amount') ?? null,
      action: pick(h, 'action') || 'warned',
      actionByName: pick(h, 'actionByName') ?? null,
      actionAt: pick(h, 'actionAt') ?? null,
      createdAt: pick(h, 'createdAt') || '',
    }));
  },

  /** "Kara listeye ekle" penceresi için siparişteki tanıtıcılar ve mevcut kayıt. */
  async prepare(subject: BlacklistSubject, branchId?: string | null): Promise<BlacklistPrepareResult> {
    const d = await call('prepare', { ...subjectBody(subject), BranchId: branchId || null }, 'Müşteri bilgisi okunamadı');
    const existing = pick(d, 'existing');
    return {
      displayName: pick(d, 'displayName') || '',
      address: pick(d, 'address') ?? null,
      platform: pick(d, 'platform') || subject.platform,
      orderRef: pick(d, 'orderRef') ?? null,
      accountId: pick(d, 'accountId') ?? null,
      keys: (pick(d, 'keys') || []).map(toKey),
      existing: existing ? toEntry(existing) : null,
    };
  },

  async create(input: BlacklistCreateInput): Promise<BlacklistEntry> {
    return toEntry(await call('create', {
      Platform: input.platform || null,
      OrderRef: input.orderRef || null,
      DisplayName: input.displayName,
      Address: input.address || null,
      AccountId: input.accountId ?? null,
      Keys: input.keys.map(keyBody),
      Reason: input.reason,
      Note: input.note || null,
      OnlyBranch: input.onlyBranch,
      BranchId: input.branchId || null,
    }, 'Kara listeye eklenemedi'));
  },

  async update(input: BlacklistUpdateInput): Promise<BlacklistEntry> {
    return toEntry(await call('update', {
      Id: input.id,
      DisplayName: input.displayName ?? null,
      Reason: input.reason ?? null,
      Note: input.note ?? null,
      OnlyBranch: input.onlyBranch ?? null,
      BranchId: input.branchId || null,
      AddKeys: (input.addKeys || []).map(keyBody),
      RemoveKeyIds: input.removeKeyIds || [],
    }, 'Kayıt güncellenemedi'));
  },

  async remove(id: number, note: string, branchId?: string | null): Promise<void> {
    await call('remove', { Id: id, Note: note, BranchId: branchId || null }, 'Kara listeden çıkarılamadı');
  },

  /** Siparişleri kara listeyle karşılaştırır. record=true: yeni sipariş, "kara listeden sipariş geldi" kaydı düşer. */
  async check(items: BlacklistSubject[], branchId?: string | null, record = false): Promise<BlacklistCheckResult[]> {
    if (items.length === 0) return [];
    const d = await call('check', { BranchId: branchId || null, Record: record, Items: items.map(subjectBody) }, 'Kara liste kontrol edilemedi');
    return (d || []).map((r: any): BlacklistCheckResult => ({
      platform: pick(r, 'platform') || '',
      orderRef: pick(r, 'orderRef') ?? null,
      match: toMatch(pick(r, 'match')),
      possible: toMatch(pick(r, 'possible')),
    }));
  },

  /** Kasiyerin kararı (hata ekranı durdurmaz). */
  async recordAction(p: { entryId: number; platform: string; orderRef: string; action: BlacklistAction; branchId?: string | null; amount?: number | null }): Promise<void> {
    try {
      await call('action', {
        EntryId: p.entryId, Platform: p.platform, OrderRef: p.orderRef, Action: p.action,
        BranchId: p.branchId || null, Amount: p.amount ?? null,
      }, 'Karar kaydedilemedi');
    } catch (e) {
      console.warn('[KARA LİSTE] Karar kaydedilemedi:', e);
    }
  },
};

// ===================== Sipariş → kara liste bilgisi =====================

const PLATFORM_OF_SOURCE: Record<string, BlacklistPlatform> = {
  phone: 'phone', yemeksepeti: 'yemeksepeti', trendyol: 'trendyol', migros: 'migros', getir: 'getir',
};

/** Paket listesi siparişinin kara liste bilgisi; QR / kiosk / e-ticaret için null. */
export function blacklistSubjectOf(order: Pick<PackageOrder, 'id' | 'source' | 'customerName' | 'customerPhone' | 'customerAddress' | 'totalAmount' | 'trendyolPackageId' | 'trendyolOrderId' | 'migrosOrderId'> | null | undefined): BlacklistSubject | null {
  if (!order) return null;
  const platform = PLATFORM_OF_SOURCE[order.source];
  if (!platform) return null;
  const orderRef = platform === 'trendyol'
    ? (order.trendyolPackageId || order.trendyolOrderId || null)
    : platform === 'migros'
      ? String(order.migrosOrderId ?? order.id)
      : String(order.id);
  return {
    platform,
    orderRef,
    name: order.customerName || null,
    phone: order.customerPhone || null,
    address: order.customerAddress || null,
    amount: order.totalAmount ?? null,
  };
}

/** Sonuç haritası anahtarı: "platform:orderRef". */
export const blacklistKeyOf = (s: Pick<BlacklistSubject, 'platform' | 'orderRef'> | null | undefined): string =>
  s?.orderRef ? `${s.platform}:${s.orderRef}` : '';

export const BLACKLIST_PLATFORM_LABELS: Record<string, string> = {
  phone: 'Telefon',
  yemeksepeti: 'Yemeksepeti',
  trendyol: 'Uber Eats',
  migros: 'Migros',
  getir: 'Getir',
};

export const BLACKLIST_PLATFORM_COLORS: Record<string, string> = {
  phone: '#2c4a7a',
  yemeksepeti: '#d6224a',
  trendyol: '#1f8a3b',
  migros: '#e07b00',
  getir: '#5d3ebc',
};
